// src/utils/parsePlace.js

/**
 * Google Places API (v1) "places/{placeId}" response parser
 * We expect the FieldMask to include at least:
 *   id, displayName, formattedAddress, addressComponents, location
 *
 * Docs: https://developers.google.com/maps/documentation/places/web-service/place-data-fields
 */
export function parsePlace(place) {
  if (!place) return {};

  const comps = Array.isArray(place.addressComponents)
    ? place.addressComponents
    : [];

  const get = (...wantedTypes) => {
    const c = comps.find((ac) =>
      ac.types?.some((t) => wantedTypes.includes(t))
    );
    if (!c) return { longText: "", shortText: "" };
    return { longText: c.longText || "", shortText: c.shortText || "" };
  };

  // Street number + route
  const streetNumber = get("street_number").longText;
  const route = get("route").longText;
  const street =
    (streetNumber && route ? `${streetNumber} ${route}` : route) ||
    place.formattedAddress ||
    "";

  // City fallbacks
  const city =
    get("locality").longText ||
    get("sublocality").longText ||
    get("administrative_area_level_2").longText ||
    "";

  // State / Province
  const stateObj =
    get("administrative_area_level_1") || get("administrative_area_level_2");
  const stateProvince = stateObj.shortText || stateObj.longText || "";

  // Postal code (+suffix if present)
  const postal = get("postal_code").longText;
  const postalSuffix = get("postal_code_suffix").longText;
  const postalCode = postalSuffix ? `${postal}-${postalSuffix}` : postal;

  // Country (prefer ISO-2 short code if you need it)
  const countryObj = get("country");
  const country = countryObj.longText || "";
  const countryCode = countryObj.shortText || "";

  // Lat/Lng
  const lat = place.location?.latitude ?? null;
  const lng = place.location?.longitude ?? null;

  return {
    street,
    city,
    stateProvince,
    postalCode,
    country,
    countryCode,
    lat,
    lng,
    displayName: place.displayName?.text || "",
    formattedAddress: place.formattedAddress || "",
  };
}
